<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useCommunicationErrorMessage } from 'dashboard/composables/useCommunicationErrorMessage';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { friendlyMessage } = useCommunicationErrorMessage();

const connection = computed(
  () => getters['communicationConnection/getCommunicationConnection'].value
);
const connectionUiFlags = computed(
  () => getters['communicationConnection/getUIFlags'].value
);
const accounts = computed(
  () => getters['communicationConnection/getAccounts'].value || []
);
const inboxes = computed(() => getters['inboxes/getInboxes'].value || []);

const isConfigured = computed(() => !!connection.value?.id);

const evolutionInboxes = computed(() =>
  inboxes.value.filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'evolution_gateway'
  )
);

const rows = computed(() =>
  accounts.value.map(account => {
    const inbox = evolutionInboxes.value.find(
      i => i.phone_number === account.phone_number
    );
    return {
      ...account,
      inbox,
      friendlyName: inbox?.name || account.gateway_instance_name,
    };
  })
);

const headers = computed(() => [
  t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.TABLE.NAME'),
  t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.TABLE.PHONE_NUMBER'),
  t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.TABLE.INSTANCE'),
  t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.TABLE.STATUS'),
  t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.TABLE.INBOX'),
  t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.TABLE.LAST_ACTIVITY'),
  t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.TABLE.ACTIONS'),
]);

const loadAccounts = async () => {
  if (!isConfigured.value) return;
  try {
    await store.dispatch('communicationConnection/fetchAccounts');
  } catch (error) {
    useAlert(friendlyMessage(error));
  }
};

const { startRefetching } = useLiveRefresh(loadAccounts, 60000);

onMounted(async () => {
  await store.dispatch('communicationConnection/get');
  await Promise.all([loadAccounts(), store.dispatch('inboxes/get')]);
  startRefetching();
});

const refresh = async () => {
  await loadAccounts();
  useAlert(t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.REFRESH_SUCCESS'));
};

const sync = async () => {
  await Promise.all([loadAccounts(), store.dispatch('inboxes/get')]);
  useAlert(t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.SYNC_SUCCESS'));
};

// View dialog
const viewDialogRef = ref(null);
const viewedAccount = ref(null);
const openView = account => {
  viewedAccount.value = account;
  viewDialogRef.value?.open();
};

// Delete binding dialog
const deleteDialogRef = ref(null);
const pendingDelete = ref(null);
const isDeleting = computed(
  () => getters['inboxes/getUIFlags'].value?.isDeleting
);
const openDelete = account => {
  pendingDelete.value = account;
  deleteDialogRef.value?.open();
};
const confirmDelete = async () => {
  try {
    await store.dispatch('inboxes/delete', pendingDelete.value.inbox.id);
    useAlert(
      t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.DELETE_DIALOG.SUCCESS')
    );
    deleteDialogRef.value?.close();
  } catch (error) {
    useAlert(
      t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.DELETE_DIALOG.ERROR')
    );
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="connectionUiFlags.isFetching"
    :loading-message="t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.HEADER')"
        :description="
          t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.DESCRIPTION')
        "
      />
      <div class="flex gap-4 mt-2 text-sm">
        <router-link
          :to="{ name: 'communication_connection_index' }"
          class="text-n-blue-11 hover:underline"
        >
          {{ t('COMMUNICATION_CONNECTION.NAV.SETTINGS') }}
        </router-link>
        <router-link
          :to="{ name: 'communication_whatsapp_accounts' }"
          class="text-n-blue-11 font-medium"
        >
          {{ t('COMMUNICATION_CONNECTION.NAV.WHATSAPP_ACCOUNTS') }}
        </router-link>
        <router-link
          :to="{ name: 'communication_inbox_mapping' }"
          class="text-n-blue-11 hover:underline"
        >
          {{ t('COMMUNICATION_CONNECTION.NAV.INBOX_MAPPING') }}
        </router-link>
      </div>
    </template>
    <template #body>
      <p v-if="!isConfigured" class="text-sm text-n-slate-11">
        {{ t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.NOT_CONFIGURED') }}
      </p>
      <template v-else>
        <div class="flex justify-end gap-2 mb-4">
          <NextButton
            faded
            slate
            sm
            :is-loading="connectionUiFlags.isFetchingAccounts"
            :label="
              t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.ACTIONS.REFRESH')
            "
            @click="refresh"
          />
          <NextButton
            faded
            blue
            sm
            :label="
              t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.ACTIONS.SYNC')
            "
            @click="sync"
          />
        </div>

        <BaseTable
          :headers="headers"
          :items="rows"
          :loading="connectionUiFlags.isFetchingAccounts"
          :no-data-message="
            t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.EMPTY')
          "
        >
          <template #row="{ items }">
            <BaseTableRow
              v-for="row in items"
              :key="row.gateway_account_id"
              :item="row"
            >
              <BaseTableCell>{{ row.friendlyName }}</BaseTableCell>
              <BaseTableCell>{{ row.phone_number || '—' }}</BaseTableCell>
              <BaseTableCell>{{ row.gateway_instance_name }}</BaseTableCell>
              <BaseTableCell>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="
                    row.connection_status === 'open'
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-ruby-3 text-n-ruby-11'
                  "
                >
                  {{ row.connection_status }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                {{
                  row.inbox
                    ? row.inbox.name
                    : t(
                        'COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.TABLE.NOT_BOUND'
                      )
                }}
              </BaseTableCell>
              <BaseTableCell>
                {{
                  row.last_seen_at ? messageTimestamp(row.last_seen_at) : '—'
                }}
              </BaseTableCell>
              <BaseTableCell>
                <div class="flex gap-1">
                  <NextButton
                    v-tooltip.top="
                      t(
                        'COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.ACTIONS.VIEW'
                      )
                    "
                    icon="i-lucide-eye"
                    slate
                    faded
                    xs
                    @click="openView(row)"
                  />
                  <NextButton
                    v-tooltip.top="
                      t(
                        'COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.ACTIONS.RECONNECT_HINT'
                      )
                    "
                    icon="i-lucide-refresh-cw"
                    slate
                    faded
                    xs
                    disabled
                  />
                  <NextButton
                    v-if="row.inbox"
                    v-tooltip.top="
                      t(
                        'COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.ACTIONS.DELETE_BINDING'
                      )
                    "
                    icon="i-lucide-trash-2"
                    ruby
                    faded
                    xs
                    @click="openDelete(row)"
                  />
                </div>
              </BaseTableCell>
            </BaseTableRow>
          </template>
        </BaseTable>
      </template>

      <Dialog
        ref="viewDialogRef"
        type="edit"
        :title="
          t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.VIEW_DIALOG.TITLE')
        "
        :show-confirm-button="false"
        :cancel-button-label="
          t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.VIEW_DIALOG.CLOSE')
        "
      >
        <dl v-if="viewedAccount" class="grid grid-cols-2 gap-3 text-sm">
          <dt class="text-n-slate-10">
            {{
              t(
                'COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.VIEW_DIALOG.GATEWAY_ACCOUNT_ID'
              )
            }}
          </dt>
          <dd class="text-n-slate-12 break-all">
            {{ viewedAccount.gateway_account_id }}
          </dd>
          <dt class="text-n-slate-10">
            {{
              t(
                'COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.VIEW_DIALOG.GATEWAY_INSTANCE_NAME'
              )
            }}
          </dt>
          <dd class="text-n-slate-12">
            {{ viewedAccount.gateway_instance_name }}
          </dd>
          <dt class="text-n-slate-10">
            {{
              t(
                'COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.VIEW_DIALOG.OWNER_JID'
              )
            }}
          </dt>
          <dd class="text-n-slate-12 break-all">
            {{ viewedAccount.owner_jid || '—' }}
          </dd>
          <dt class="text-n-slate-10">
            {{
              t(
                'COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.VIEW_DIALOG.PROFILE_NAME'
              )
            }}
          </dt>
          <dd class="text-n-slate-12">
            {{ viewedAccount.profile_name || '—' }}
          </dd>
          <dt class="text-n-slate-10">
            {{
              t(
                'COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.VIEW_DIALOG.INTEGRATION'
              )
            }}
          </dt>
          <dd class="text-n-slate-12">
            {{ viewedAccount.integration || '—' }}
          </dd>
        </dl>
      </Dialog>

      <Dialog
        ref="deleteDialogRef"
        type="alert"
        :title="
          t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.DELETE_DIALOG.TITLE')
        "
        :description="
          pendingDelete
            ? t(
                'COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.DELETE_DIALOG.MESSAGE',
                { name: pendingDelete.friendlyName }
              )
            : ''
        "
        :is-loading="isDeleting"
        :confirm-button-label="
          t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.DELETE_DIALOG.CONFIRM')
        "
        :cancel-button-label="
          t('COMMUNICATION_CONNECTION.WHATSAPP_ACCOUNTS.DELETE_DIALOG.CANCEL')
        "
        @confirm="confirmDelete"
      />
    </template>
  </SettingsLayout>
</template>
